import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const STATUS_MAP = {
  SUBMITTED:    { label: "· Pending PMO", color: "#D6DF27",  bg: "rgba(214,223,39,0.10)"  },
  MOVE_PENDING: { label: "· Move Pending", color: "#c88a3a", bg: "rgba(200,138,58,0.12)"  },
  PMO_APPROVED: { label: "· At SCG",      color: "#28B3B4",  bg: "rgba(40,179,180,0.12)"  },
  ACTIVE:       { label: "· Active",      color: "#4a9960",  bg: "rgba(74,153,96,0.12)"   },
  ENDED:        { label: "· Ended",       color: "#7A9E9C",  bg: "rgba(120,120,120,0.10)" },
  REJECTED:     { label: "· Rejected",    color: "#c84040",  bg: "rgba(200,64,64,0.10)"   },
};

const PARENT_STATUS_MAP = {
  SUBMITTED:    { label: "Pending PMO", color: "#D6DF27" },
  PMO_APPROVED: { label: "At SCG",      color: "#28B3B4" },
  ACTIVE:       { label: "Active",      color: "#4a9960" },
  ENDED:        { label: "Ended",       color: "#7A9E9C" },
  REJECTED:     { label: "Rejected",    color: "#c84040" },
};

const getInitials = (name = "") => {
  const p = name.trim().split(" ");
  return p.length === 1 ? p[0].slice(0, 2).toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = ["#28B3B4", "#D6DF27", "#4a9960", "#7a5abd", "#c88a3a"];
const avatarColor = (name = "") =>
  AVATAR_COLORS[((name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0)) % AVATAR_COLORS.length];

const timeAgo = (isoString) => {
  if (!isoString) return "";
  const diff = Date.now() - new Date(isoString).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days  = Math.floor(hours / 24);
  if (days > 0)  return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return `${mins}m ago`;
};

const MetaCell = ({ label, value, t }) => (
  <Box>
    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.12em", textTransform: "uppercase", color: t.text3, mb: "2px" }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", color: t.text2 }}>
      {value || "—"}
    </Typography>
  </Box>
);

const ApprovalCard = ({ request, onReview }) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const status    = STATUS_MAP[request.status] || STATUS_MAP.SUBMITTED;
  const color     = avatarColor(request.associateName);
  const initials  = getInitials(request.associateName);
  const submitted = timeAgo(request.submittedAt);

  // Parent request fields — present when this is a resubmission
const isResubmit   = !!request.parentRequestId && request.parentRequestStatus === "REJECTED";
  const isAmendment  = !!request.parentRequestId &&
    (request.parentRequestStatus === "ACTIVE" || request.parentRequestStatus === "SUPERSEDED") &&
    request.status !== "MOVE_PENDING";
  const isMove       = request.status === "MOVE_PENDING";
  const isFteChange  = isAmendment && request.parentFte != null && request.parentFte !== request.fte;
  const isDateChange = isAmendment && !isFteChange && request.parentStartDate != null &&
    (request.parentStartDate !== request.startDate || request.parentEndDate !== request.endDate);
  const parentStatus = request.parentRequestStatus
    ? (PARENT_STATUS_MAP[request.parentRequestStatus] ?? { label: request.parentRequestStatus, color: t.text3 })
    : null;

  return (
    <Box sx={{
      border: `1px solid ${t.border2}`,
      borderRadius: "4px",
      mb: "8px",
      overflow: "hidden",
      background: t.surface,
      transition: "border-color 0.15s",
      "&:hover": { borderColor: t.border3 },
    }}>

      {/* ── Resubmit banner — only shown when parentRequestId is set ── */}
      {isResubmit && (
        <Box sx={{
          display: "flex", alignItems: "center", gap: "10px",
          padding: "6px 16px 6px 20px",
          background: isDark ? "rgba(214,223,39,0.07)" : "rgba(214,223,39,0.08)",
          borderBottom: `1px solid rgba(214,223,39,0.2)`,
        }}>
          {/* "Resubmitted" pill */}
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            padding: "2px 8px", borderRadius: "2px",
            background: "rgba(214,223,39,0.15)",
            border: "1px solid rgba(214,223,39,0.35)",
          }}>
            <Typography sx={{
              fontFamily: "'DM Mono', monospace", fontSize: "8px",
              color: t.goldText ?? "#6B7300", letterSpacing: "0.1em",
              fontWeight: 700, textTransform: "uppercase",
            }}>
              ↺ Resubmitted
            </Typography>
          </Box>

          <Typography sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "9px", color: t.text3,
          }}>
            Replaces
          </Typography>

          {/* Clickable parent request code */}
          <Box
            onClick={() => onReview?.({ requestId: request.parentRequestId, requestCode: request.parentRequestCode, associateName: request.associateName, roleTitle: request.roleTitle, status: request.parentRequestStatus })}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              cursor: "pointer",
              fontFamily: "'DM Mono', monospace", fontSize: "9px",
              color: t.accent,
              textDecoration: "underline",
              textDecorationColor: `${t.accent}55`,
              "&:hover": { textDecorationColor: t.accent },
            }}
          >
            {request.parentRequestCode}
          </Box>

          {/* Parent status badge */}
          {parentStatus && (
            <Box sx={{
              display: "inline-flex", alignItems: "center",
              padding: "1px 7px", borderRadius: "2px",
              background: `${parentStatus.color}18`,
              border: `1px solid ${parentStatus.color}44`,
            }}>
              <Typography sx={{
                fontFamily: "'DM Mono', monospace", fontSize: "8px",
                color: parentStatus.color, letterSpacing: "0.06em",
              }}>
                {parentStatus.label}
              </Typography>
            </Box>
          )}

          {/* Parent FTE / dates diff — shown if values changed */}
          {request.parentFte != null && request.parentFte !== request.fte && (
            <Typography sx={{
              fontFamily: "'DM Mono', monospace", fontSize: "9px",
              color: t.text3, ml: "auto",
            }}>
              was {request.parentFte} FTE
            </Typography>
          )}
        </Box>
      )}

      {/* Amendment banner */}
{isAmendment && (
  <Box sx={{
    display: "flex", alignItems: "center", gap: "10px",
    padding: "6px 16px 6px 20px",
    background: isDark ? "rgba(40,179,180,0.06)" : "rgba(40,179,180,0.07)",
    borderBottom: "1px solid rgba(40,179,180,0.18)",
  }}>
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      padding: "2px 8px", borderRadius: "2px",
      background: "rgba(40,179,180,0.15)",
      border: "1px solid rgba(40,179,180,0.35)",
    }}>
      <Typography sx={{
        fontFamily: "'DM Mono', monospace", fontSize: "8px",
        color: "#1a7a7b", letterSpacing: "0.1em",
        fontWeight: 700, textTransform: "uppercase",
      }}>
        {isFteChange ? "\u27f3 FTE Amendment" : isDateChange ? "\ud83d\udcc5 Date Change" : "\u27f3 Amendment"}
      </Typography>
    </Box>
    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
      of
    </Typography>
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      cursor: "pointer",
      fontFamily: "'DM Mono', monospace", fontSize: "9px",
      color: t.accent,
      textDecoration: "underline",
      textDecorationColor: `${t.accent}55`,
      "&:hover": { textDecorationColor: t.accent },
    }}
      onClick={() => onReview?.({ requestId: request.parentRequestId, requestCode: request.parentRequestCode, associateName: request.associateName, roleTitle: request.roleTitle, status: request.parentRequestStatus })}
    >
      {request.parentRequestCode}
    </Box>
    {isFteChange && (
      <Box sx={{
        padding: "1px 7px", borderRadius: "2px",
        background: "rgba(40,179,180,0.10)",
        border: "1px solid rgba(40,179,180,0.25)",
      }}>
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: "#28B3B4" }}>
          FTE {request.parentFte} → {request.fte}
        </Typography>
      </Box>
    )}
    {isDateChange && (
      <Box sx={{
        padding: "1px 7px", borderRadius: "2px",
        background: "rgba(40,179,180,0.10)",
        border: "1px solid rgba(40,179,180,0.25)",
      }}>
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: "#28B3B4" }}>
          {request.parentStartDate} → {request.parentEndDate}  ⟶  {request.startDate} → {request.endDate}
        </Typography>
      </Box>
    )}
  </Box>
)}

      {/* Move banner */}
      {isMove && (
        <Box sx={{
          display: "flex", alignItems: "center", gap: "10px",
          padding: "6px 16px 6px 20px",
          background: isDark ? "rgba(200,138,58,0.07)" : "rgba(200,138,58,0.08)",
          borderBottom: "1px solid rgba(200,138,58,0.22)",
        }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            padding: "2px 8px", borderRadius: "2px",
            background: "rgba(200,138,58,0.15)",
            border: "1px solid rgba(200,138,58,0.35)",
          }}>
            <Typography sx={{
              fontFamily: "'DM Mono', monospace", fontSize: "8px",
              color: "#c88a3a", letterSpacing: "0.1em",
              fontWeight: 700, textTransform: "uppercase",
            }}>
              ⇄ Move
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
            from
          </Typography>
          <Box
            onClick={() => onReview?.({ requestId: request.parentRequestId, requestCode: request.parentRequestCode, associateName: request.associateName, roleTitle: request.roleTitle, status: request.parentRequestStatus })}
            sx={{
              cursor: "pointer",
              fontFamily: "'DM Mono', monospace", fontSize: "9px",
              color: t.accent, textDecoration: "underline",
              textDecorationColor: `${t.accent}55`,
              "&:hover": { textDecorationColor: t.accent },
            }}
          >
            {request.parentRequestCode}
          </Box>
          {parentStatus && (
            <Box sx={{
              display: "inline-flex", alignItems: "center",
              padding: "1px 7px", borderRadius: "2px",
              background: `${parentStatus.color}18`,
              border: `1px solid ${parentStatus.color}44`,
            }}>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: parentStatus.color, letterSpacing: "0.06em" }}>
                {parentStatus.label}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ── Left accent bar + main content ── */}

      {/* ── Left accent bar + main content ── */}
      <Box sx={{ display: "flex" }}>
        <Box sx={{ width: "3px", flexShrink: 0, background: status.color }} />

        <Box sx={{ flex: 1, padding: "12px 16px" }}>

          {/* Top row */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "8px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3 }}>
                {request.requestCode}
              </Typography>
              <Box sx={{
                background: request.positionType === "BIL" ? t.accent2 : t.gold2,
                border: `1px solid ${request.positionType === "BIL" ? t.accent + "44" : t.gold + "44"}`,
                borderRadius: "2px", padding: "1px 7px",
              }}>
                <Typography sx={{
                  fontFamily: "'DM Mono', monospace", fontSize: "8px",
                  color: request.positionType === "BIL" ? t.accent : t.gold,
                  letterSpacing: "0.1em", fontWeight: 600,
                }}>
                  {request.positionType === "BIL" ? "BILLED" : "UNBILLED"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Box sx={{ display: "inline-flex", alignItems: "center", background: status.bg, borderRadius: "3px", padding: "2px 8px" }}>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: status.color, fontWeight: 600, letterSpacing: "0.08em" }}>
                  {status.label}
                </Typography>
              </Box>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3, mt: "3px" }}>
                {submitted ? `Submitted ${submitted}` : "Submitted"}
              </Typography>
            </Box>
          </Box>

          {/* Associate row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "6px" }}>
            <Box sx={{
              width: "28px", height: "28px", borderRadius: "50%", flexShrink: 0,
              background: `${color}22`, border: `1px solid ${color}55`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'DM Mono', monospace", fontSize: "9px", fontWeight: 700, color,
            }}>
              {initials}
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 600, color: t.text }}>
                  {request.associateName}
                </Typography>
                <Box sx={{ background: t.surface3, borderRadius: "2px", padding: "1px 6px" }}>
                  <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
                    {request.associateLevelCode}
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: "11px", color: t.text3, mt: "1px" }}>
                Requested for:{" "}
                <strong style={{ color: t.text }}>{request.roleTitle}</strong>
                {" · "}
                <span style={{ color: t.accent, fontFamily: "'DM Mono', monospace", fontSize: "10px" }}>
                  {request.positionCode}
                </span>
                {" · "}
                {request.sowName}
                {request.accountName && (
                  <span style={{ color: t.text3 }}> · {request.accountName}</span>
                )}
              </Typography>
            </Box>

            <Box sx={{ ml: "auto", textAlign: "right", flexShrink: 0 }}>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "16px", fontWeight: 700, color: t.accent, lineHeight: 1 }}>
                {request.fte} FTE
              </Typography>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, mt: "2px" }}>
                {request.startDate} → {request.endDate}
              </Typography>
            </Box>
          </Box>

          {/* Meta row */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", pt: "10px", borderTop: `1px solid ${t.border}`, mt: "8px" }}>
            <MetaCell label="SOW - Account" value={request.accountName ? `${request.sowName} · ${request.accountName}` : request.sowName} t={t} />
            <MetaCell label="MAC"          value={request.macName}         t={t} />
            <MetaCell label="Submitted By" value={request.submittedByName} t={t} />
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: "12px",
        padding: "8px 16px 10px 20px",
        borderTop: `1px solid ${t.border}`,
        background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
      }}>
        <Box
          onClick={() => onReview?.(request)}
          sx={{
            display: "flex", alignItems: "center", gap: "5px",
            padding: "5px 14px", borderRadius: "3px", cursor: "pointer",
            background: t.accent2, border: `1px solid ${t.accent}44`,
            fontFamily: "'DM Mono', monospace", fontSize: "10px",
            color: t.accent, fontWeight: 600,
            transition: "all 0.15s",
            "&:hover": { background: t.accent, color: isDark ? "#060E0D" : "#fff" },
          }}
        >
          Review →
        </Box>
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3, letterSpacing: "0.04em" }}>
          Click to open full detail
        </Typography>
      </Box>
    </Box>
  );
};

export default ApprovalCard;